/* eslint-disable import/no-extraneous-dependencies */
import * as React from "react"
import dynamic from "next/dynamic"
import tw, { styled } from "twin.macro"

import { When } from "@components/If"
import type { TippyProps } from "@tippyjs/react"

import "tippy.js/dist/tippy.css"

const Tippy = dynamic(() => import("@tippyjs/react"))

interface WithInverse {
    inverse?: boolean
}

const Wrapper = styled.div`
    width: fit-content;
`

const Title = styled.span<WithInverse>`
    ${tw`title-5 text-bold dark:text-dark-bold`}
    ${({ inverse }) => inverse && tw`text-dark-bold dark:text-bold`}
`

const SupportText = styled.p<WithInverse>`
    ${tw`paragraph-4 font-normal text-additional dark:text-dark-additional`}
    ${({ inverse }) => inverse && tw`text-dark-additional dark:text-additional`}
`

const StyledTippy = styled(Tippy)<WithInverse>`
    ${tw`
        rounded-xs p-1
        shadow-lg
        dark:shadow-dark-lg
        bg-background dark:bg-[#0A0A0A]
    `}
    ${({ inverse }) => inverse && tw`!bg-[#0A0A0A] dark:!bg-background`}
    
    
    
    .tippy-arrow {
        ${tw`text-white dark:text-[#0A0A0A]`}
        ${({ inverse }) => inverse && tw`text-[#0A0A0A] dark:text-white`}
    }
`

export interface TooltipProps extends React.PropsWithChildren<TippyProps> {
    /**
     * Title tooltip content
     * @default undefined
     * @type string
     *
     */
    title: string | React.ReactNode
    /**
     * Supporting text tooltip content
     * @default undefined
     * @type string
     */
    supportingText?: string | React.ReactNode
    /**
     * Tooltip className
     */
    className?: string
    /**
     * Inverse tooltip color
     */
    inverse?: boolean
}

/**
 * Tooltips show contextual help or information about specific components when a user hovers on them.
 *
 * @guidelines
 * 1. Should never contain critical information that a user would absolutely need to proceed.
 * 2. Use when the description content would be too much information to include inline or create too much clutter. For example, when expert users have seen it many times.
 *
 * @example
 * <Tooltip content="Tooltip content">
 *    <Button>Hover me</Button>
 * </Tooltip>
 *
 */
const Tooltip: React.FC<TooltipProps> = ({
    title,
    supportingText,
    placement,
    children,
    className,
    offset,
    popperOptions,
    arrow,
    inverse,
    interactive,
    interactiveBorder
}: TooltipProps) => (
    <StyledTippy
        content={
            <div className='flex flex-col gap-1'>
                <Title inverse={inverse}>{title}</Title>
                <When condition={supportingText}>
                    <SupportText inverse={inverse}>{supportingText}</SupportText>
                </When>
            </div>
        }
        placement={placement}
        hideOnClick={false}
        offset={offset}
        popperOptions={popperOptions}
        arrow={arrow}
        inverse={inverse}
        interactiveBorder={interactiveBorder}
        interactive={interactive}
        className='reku-new'
    >
        <Wrapper className={className}>{children}</Wrapper>
    </StyledTippy>
)

Tooltip.defaultProps = {
    placement: "auto",
    supportingText: undefined,
    className: undefined,
    inverse: false
}

export default Tooltip
